import { PermissionName } from '@sironamedical/common/constants/permissionName';
import { FF, useFeatureFlagEnabled } from 'modules/feature-flags';
import { useValidateUserHasRbacPermissions } from 'hooks/useValidateUserHasRbacPermissions';

/** Shared message shown on claim controls that are disabled because the user lacks CLAIM_EXAM. */
export const CLAIM_DISABLED_TOOLTIP =
  'Your role is not configured to claim exams. Contact your administrator.';

/** Shared message shown on Start Read when the exam is already claimed by another user. */
export const LOCKED_CASE_START_READ_WARNING =
  'Exam cannot be claimed since it is currently being read by another user.';

/**
 * Whether the current user holds the CLAIM_EXAM RBAC permission, read from the existing
 * `permissions` scalar via the standard RBAC hook (the same pattern every other permission uses).
 *
 * This is the raw permission only — it does NOT account for the enforcement rollout flag. For UI
 * gating use {@link useHasClaimExamPermission}, which composes this with the flag.
 */
export const useHasClaimExamRbacPermission = (): boolean =>
  useValidateUserHasRbacPermissions({ permissionNames: [PermissionName.ClaimExam] });

/**
 * Whether the current user holds the permission to claim exams.
 *
 * This is a permission gate only — holding it does NOT mean a given exam is actually claimable;
 * that still depends on per-item state (lock status, claimed-by-another-user, etc.) layered on top
 * by callers such as `getCaseClaimStatus`.
 *
 * CLAIM_EXAM RBAC enforcement is opt-in via the `ENFORCE_CLAIM_EXAM_RBAC` feature flag:
 *  - flag off / undefined (the default, in code and in Harness): RBAC is ignored — every user can
 *    claim, exactly as today. This is the safety net against a backend-only rollback of the
 *    permission.
 *  - flag on: the CLAIM_EXAM permission is respected; only users who hold it can claim.
 *
 * In other words, the permission is withheld from a user only when BOTH the flag is on AND they
 * lack it. Follow-up: remove the flag (and this composition) once enforcement is fully rolled out.
 * See RP-5761.
 */
export const useHasClaimExamPermission = (): boolean => {
  const [isRbacEnforced] = useFeatureFlagEnabled(FF.ENFORCE_CLAIM_EXAM_RBAC);
  const hasRbacPermission = useHasClaimExamRbacPermission();

  return !isRbacEnforced || hasRbacPermission;
};
